import React, { useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';

export default function DiagnosticForm({ 
  mainTab, 
  intelligenceTab, 
  configTab, 
  formData, 
  setFormData, 
  onSubmit, 
  onClose, 
  saving, 
  data, 
  flatSubdomains 
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  const modal = (
    <div className="admin-modal-overlay">
      <motion.form
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onSubmit={onSubmit}
        className="admin-modal-form"
      >
        <div className="admin-modal-header">
          <h2 className="titre-carte">
            {mainTab === 'config' && configTab === 'niveaux' && (formData.id ? 'Modifier le niveau' : 'Nouveau niveau')}
            {mainTab === 'config' && configTab === 'livrables' && (formData.id ? 'Modifier le livrable' : 'Nouveau livrable')}
            {mainTab === 'intelligence' && intelligenceTab === 'ACTIONS' && (formData.id ? 'Modifier l\'action' : 'Nouvelle action')}
            {mainTab === 'intelligence' && intelligenceTab !== 'ACTIONS' && (formData.id ? 'Modifier le constat' : 'Nouveau constat')}
          </h2>
          <button type="button" onClick={onClose} className="act act-icone act-tertiaire"><X className="w-5 h-5 text-[var(--text-muted)]" /></button>
        </div>
        
        <div className="admin-modal-content space-y-5">
          {mainTab === 'config' && configTab === 'niveaux' && (
            <>
              <div className="space-y-1.5">
                <label>Libellé du niveau</label>
                <input required className="champ font-bold" value={formData.libelle} onChange={e => setFormData({ ...formData, libelle: e.target.value })} placeholder="Ex. Initial, Mature, Expert..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label>Score min (%)</label><input type="number" required min={0} max={100} className="champ" value={formData.min_score} onChange={e => setFormData({ ...formData, min_score: e.target.value })} /></div>
                <div className="space-y-1.5"><label>Score max (%)</label><input type="number" required min={0} max={100} className="champ" value={formData.max_score} onChange={e => setFormData({ ...formData, max_score: e.target.value })} /></div>
              </div>
            </>
          )}
          
          {mainTab === 'config' && configTab === 'livrables' && (
            <>
              <div className="space-y-1.5"><label>Livrable</label><textarea required className="champ" rows={3} placeholder="Ce qui atteste que l'action est faite…" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label>Sous-domaine</label><select required className="champ" value={formData.sous_domaine_id} onChange={e => setFormData({ ...formData, sous_domaine_id: e.target.value, action_id: '' })}>{flatSubdomains.map(sd => <option key={sd.id} value={sd.id}>{sd.nom}</option>)}</select></div>
                <div className="space-y-1.5"><label>Niveau</label><select required className="champ" value={formData.niveau_id} onChange={e => setFormData({ ...formData, niveau_id: e.target.value, action_id: '' })}>{data.niveaux.map(n => <option key={n.id} value={n.id}>{n.libelle}</option>)}</select></div>
              </div>
              {/* L'action que ce livrable atteste : coché par le coach dans le
                  plan, il dit au système que l'action est faite. */}
              <div className="space-y-1.5"><label>Action attestée</label>
                <select className="champ" value={formData.action_id || ''} onChange={e => setFormData({ ...formData, action_id: e.target.value })}>
                  <option value="">— Aucune —</option>
                  {data.actions.filter(a => String(a.sous_domaine_id) === String(formData.sous_domaine_id) && String(a.niveau_id) === String(formData.niveau_id))
                    .map(a => <option key={a.id} value={a.id}>{a.ordre}. {(a.description || '').slice(0, 90)}</option>)}
                </select>
              </div>
            </>
          )}
          
          {mainTab === 'intelligence' && intelligenceTab === 'ACTIONS' && (
            <>
              {/* Une action appartient à un niveau d'un sous-domaine : elle fait
                  passer au niveau suivant. */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label>Sous-domaine</label><select required className="champ" value={formData.sous_domaine_id} onChange={e => setFormData({ ...formData, sous_domaine_id: e.target.value })}><option value="">Sélectionner...</option>{flatSubdomains.map(sd => <option key={sd.id} value={sd.id}>{sd.nom}</option>)}</select></div>
                <div className="space-y-1.5"><label>Niveau</label><select required className="champ" value={formData.niveau_id} onChange={e => setFormData({ ...formData, niveau_id: e.target.value })}><option value="">Sélectionner...</option>{data.niveaux.map(n => <option key={n.id} value={n.id}>{n.libelle}</option>)}</select></div>
              </div>
              <div className="space-y-1.5"><label>Action corrective recommandée</label><textarea required className="champ font-medium" rows={4} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Décrivez l'action stratégique à mener..." /></div>
            </>
          )}
          
          {mainTab === 'intelligence' && intelligenceTab !== 'ACTIONS' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label>Sous-domaine concerné</label><select required className="champ" value={formData.sous_domaine_id} onChange={e => setFormData({ ...formData, sous_domaine_id: e.target.value })}><option value="">Sélectionner...</option>{flatSubdomains.map(sd => <option key={sd.id} value={sd.id}>{sd.nom}</option>)}</select></div>
                <div className="space-y-1.5"><label>Niveau de maturité</label><select required className="champ" value={formData.niveau_id} onChange={e => setFormData({ ...formData, niveau_id: e.target.value })}><option value="">Sélectionner...</option>{data.niveaux.map(n => <option key={n.id} value={n.id}>{n.libelle}</option>)}</select></div>
              </div>
              <div className="space-y-1.5"><label>Description du constat expert</label><textarea required className="champ font-bold" rows={4} placeholder="Ex. Le conseil d'administration est mobilisé et assure un suivi rigoureux..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} /></div>
            </>
          )}
        </div>
        
        <div className="admin-modal-footer">
          <button type="button" onClick={onClose} className="act act-tertiaire flex-1">Annuler</button>
          <button type="submit" disabled={saving} aria-busy={saving || undefined} className="act flex-[2]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </motion.form>
    </div>
  );

  return createPortal(modal, document.body);
}
